import { useEffect } from 'react'
import {
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { login, register, fetchCurrentUser, logout } from '@/features/auth/api/auth-api'
import type { LoginPayload, RegisterPayload, AuthUser } from '@/features/auth/types'
import { useAuthStore } from '@/features/auth/stores/auth-store'
import { ApiError } from '@/shared/api/http-client'
import i18n from '@/shared/i18n'

export const currentUserQueryKey = ['current-user']

type CurrentUserQueryResult = Omit<UseQueryResult<AuthUser, ApiError>, 'then'>

export function useCurrentUserQuery(options?: { enabled?: boolean }): CurrentUserQueryResult {
  const token = useAuthStore((state) => state.token)
  const setUser = useAuthStore((state) => state.setUser)
  const reset = useAuthStore((state) => state.reset)
  const enabled = options?.enabled ?? Boolean(token)

  const query = useQuery<AuthUser, ApiError>({
    queryKey: currentUserQueryKey,
    queryFn: () => fetchCurrentUser(),
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!query.data) return
    const user = query.data
    setUser(user)
    if (user.preferredLanguage) {
      void i18n.changeLanguage(user.preferredLanguage)
    }
  }, [query.data, setUser])

  useEffect(() => {
    const err = query.error
    if (!(err instanceof ApiError) || err.status !== 401) return
    reset()
  }, [query.error, reset])

  const { then: _omitPromiseLike, ...rest } = query as UseQueryResult<AuthUser, ApiError> & {
    then?: undefined
  }

  void _omitPromiseLike

  return rest
}

export function useBootstrapCurrentUser(enabled?: boolean) {
  void useCurrentUserQuery({ enabled })
}

export function useLoginMutation() {
  const queryClient = useQueryClient()
  const setSession = useAuthStore((state) => state.setSession)

  return useMutation({
    mutationFn: (payload: LoginPayload) => login(payload),
    onSuccess: async (response) => {
      setSession(response.token)
      await queryClient.invalidateQueries({ queryKey: currentUserQueryKey })
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  const reset = useAuthStore((state) => state.reset)

  return useMutation({
    mutationFn: () => logout(),
    onSettled: async () => {
      reset()
      await queryClient.invalidateQueries({ queryKey: currentUserQueryKey })
    },
  })
}
export function useRegisterMutation() {
  return useMutation({
    mutationFn: (payload: RegisterPayload) => register(payload),
  })
}
